import { Button, H4, L88, Stack } from "@adfenix/alexandria";
import { useFieldArray, useFormContext } from "react-hook-form";
import { BundleGroupOverviewItem } from "./BundleGroupOverviewItem";
import { CreateOrEditBundle } from "./CreateOrEditBundle";
import Divider from "components/Divider";
import { EditBundleGroup } from "./EditBundleGroup";
import { Modal } from "components/Modal";
import styled from "styled-components";
import { useState } from "react";
import { ProgramService } from "@adfenix/types";
import { useDisabledFields } from "../useDisabledFields";
import { useTranslation } from "react-i18next";

export const formMaxWidth = 980;

const ProgramBundle = styled(Stack)`
  border: 1px solid ${({ theme }) => L88(theme.colors.grey)};
  ${({ theme }) => theme.borderRadius.m};
`;

export const ProgramBundles = () => {
  const { t } = useTranslation();

  const disabledFields = useDisabledFields();
  const [snapshot, setSnapshot] = useState<ProgramService.ProgramModel>();
  const [showModal, setShowModal] = useState(false);
  const [editBundleGroupIndex, setEditBundleGroupIndex] = useState<
    number | null
  >();
  const [isNew, setIsNew] = useState(false);
  const [isNewBundle, setIsNewBundle] = useState(false);

  const [selectedBundleGroupIndex, setSelectedBundleGroupIndex] = useState<
    undefined | number
  >(undefined);

  const [selectedBundleIndex, setSelectedBundleIndex] = useState<
    undefined | number
  >(undefined);

  const { watch, reset, control } =
    useFormContext<ProgramService.ProgramModel>();
  const { append } = useFieldArray({
    name: "bundleGroups",
    control
  });

  const allData = watch();
  const bundleGroups = allData.bundleGroups ?? [];

  const editBundleGroupWithIndex = (index: number) => {
    setIsNew(false);
    createSnapshot();
    setEditBundleGroupIndex(index);
  };

  const cancelBundleGroupEdit = () => {
    setEditBundleGroupIndex(null);
    reset(snapshot);
  };

  const cancelBundleEdit = () => {
    setShowModal(false);
    reset(snapshot);
  };

  const createSnapshot = () => {
    const newData = JSON.parse(JSON.stringify(allData));

    setSnapshot(newData);
  };

  return (
    <>
      {selectedBundleGroupIndex !== undefined && (
        <Modal show={showModal} onClose={() => cancelBundleEdit()}>
          <CreateOrEditBundle
            setShowModal={setShowModal}
            bundleGroupIndex={selectedBundleGroupIndex ?? 0}
            onCancel={cancelBundleEdit}
            isNew={isNewBundle}
            bundleIndex={selectedBundleIndex ?? 0}
          />
        </Modal>
      )}
      {editBundleGroupIndex !== null && editBundleGroupIndex !== undefined && (
        <Modal show={true} onClose={cancelBundleGroupEdit}>
          <EditBundleGroup
            bundleGroupIndex={editBundleGroupIndex}
            onSave={() => {
              setEditBundleGroupIndex(null);
            }}
            isNew={isNew}
            onCancel={cancelBundleGroupEdit}
          />
        </Modal>
      )}
      <H4>{t("programBuilder.program.details.overview.table.header")}</H4>
      {t("programBuilder.program.details.overview.table.subHeader")}
      <Divider />
      <ProgramBundle p="m" spacing="m">
        {bundleGroups
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((bundleGroup, index) => (
            <BundleGroupOverviewItem
              editBundleGroup={editBundleGroupWithIndex}
              bundleGroup={bundleGroup}
              bundleGroupIndex={index}
              key={bundleGroup.id ?? index}
              onAction={(bundleIndex: number) => {
                setIsNewBundle(false);
                setSelectedBundleIndex(bundleIndex);
                setSelectedBundleGroupIndex(index);
                setShowModal(true);
              }}
              createSnapshot={createSnapshot}
              setIsNewBundle={setIsNewBundle}
            />
          ))}
      </ProgramBundle>
      <Button
        logLabel="add-bundle-group-button"
        variant="text"
        onClick={() => {
          setIsNew(true);
          createSnapshot();
          append({
            legacyType: "HomeBooster",
            name: "",
            default: true,
            sortOrder:
              bundleGroups.length > 0
                ? Math.max(...bundleGroups.map(x => x.sortOrder)) + 1
                : 1
          });
          setEditBundleGroupIndex(bundleGroups.length);
        }}
        disabled={disabledFields("bundleGroups")}
      >
        {t(
          "programBuilder.program.details.overview.table.button.addBundleGroup"
        )}
      </Button>
    </>
  );
};
